<template>
  <div class="fixed inset-0 z-50 overflow-y-auto" v-if="show">
    <div
      class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
    >
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="$emit('close')"
      ></div>
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full"
      >
        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="sm:flex sm:items-start">
            <div class="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
                {{
                  mode === 'create'
                    ? 'Создать договор'
                    : 'Редактировать договор'
                }}
              </h3>

              <form @submit.prevent="submitForm" class="space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Номер договора *
                    </label>
                    <input
                      v-model="form.number_of_contract"
                      type="text"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.number_of_contract
                          ? 'border-red-500 bg-red-50'
                          : !form.number_of_contract?.trim()
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    />
                    <p
                      v-if="validationErrors.number_of_contract"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.number_of_contract }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Дата договора *
                    </label>
                    <input
                      v-model="form.date_of_contract"
                      type="date"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.date_of_contract
                          ? 'border-red-500 bg-red-50'
                          : !form.date_of_contract
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    />
                    <p
                      v-if="validationErrors.date_of_contract"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.date_of_contract }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Юридическое название *
                    </label>
                    <input
                      v-model="form.legal_name"
                      type="text"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.legal_name
                          ? 'border-red-500 bg-red-50'
                          : !form.legal_name?.trim()
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    />
                    <p
                      v-if="validationErrors.legal_name"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.legal_name }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Контрагент *
                    </label>
                    <select
                      v-model="form.counterparty_id"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.counterparty_id
                          ? 'border-red-500 bg-red-50'
                          : !form.counterparty_id
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                      :disabled="counterpartiesLoading"
                    >
                      <option value="">
                        {{
                          counterpartiesLoading
                            ? 'Загрузка...'
                            : 'Выберите контрагента'
                        }}
                      </option>
                      <option
                        v-for="counterparty in counterparties"
                        :key="counterparty.id"
                        :value="counterparty.id"
                      >
                        {{ counterparty.name }}
                      </option>
                    </select>
                    <p
                      v-if="validationErrors.counterparty_id"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.counterparty_id }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Платежный аккаунт *
                    </label>
                    <input
                      list="payment-accounts-list"
                      v-model="form.payment_account_name"
                      @input="updatePaymentAccountId"
                      placeholder="Поиск по названию..."
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.payment_account_name
                          ? 'border-red-500 bg-red-50'
                          : !form.payment_account_name?.trim()
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                      :disabled="paymentAccountsLoading"
                      required
                    />
                    <datalist id="payment-accounts-list">
                      <option value="">
                        {{
                          paymentAccountsLoading
                            ? 'Загрузка...'
                            : 'Выберите платежный аккаунт'
                        }}
                      </option>
                      <option
                        v-for="account in paymentAccounts"
                        :key="account.id"
                        :value="account.name"
                      >
                        {{ account.name }}
                      </option>
                    </datalist>
                    <p
                      v-if="validationErrors.payment_account_name"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.payment_account_name }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Валюта *
                    </label>
                    <select
                      v-model="form.currency"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.currency
                          ? 'border-red-500 bg-red-50'
                          : !form.currency
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    >
                      <option value="">Выберите валюту</option>
                      <option
                        v-for="currency in currencies"
                        :key="currency.value"
                        :value="currency.value"
                      >
                        {{ currency.label }}
                      </option>
                    </select>
                    <p
                      v-if="validationErrors.currency"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.currency }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Тип организации *
                    </label>
                    <select
                      v-model="form.type_organization"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.type_organization
                          ? 'border-red-500 bg-red-50'
                          : !form.type_organization
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    >
                      <option
                        v-for="orgType in organizationTypes"
                        :key="orgType.value"
                        :value="orgType.value"
                      >
                        {{ orgType.label }}
                      </option>
                    </select>
                    <p
                      v-if="validationErrors.type_organization"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.type_organization }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Тип закупки
                    </label>
                    <input
                      v-model="form.type_of_purchase"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Консультационная ставка (час)
                    </label>
                    <input
                      v-model="form.consulting_hourly_rate"
                      type="number"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Срок оплаты после выставления счета (дни)
                    </label>
                    <input
                      v-model="form.deadline_after_invoice"
                      type="number"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Прогноз оплаты после выставления счета (дни)
                    </label>
                    <input
                      v-model="form.forecast_payment_after_invoice"
                      type="number"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Период оплаты
                    </label>
                    <select
                      v-model="form.is_by_month"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option :value="false">Период</option>
                      <option :value="true">Помесячная</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Период оплаты (начало) *
                    </label>
                    <input
                      v-model="form.payment_period_start"
                      type="date"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                        validationErrors.payment_period_start
                          ? 'border-red-500 bg-red-50'
                          : !form.payment_period_start
                            ? 'border-orange-300 bg-orange-50'
                            : 'border-gray-300'
                      ]"
                    />
                    <p
                      v-if="validationErrors.payment_period_start"
                      class="mt-1 text-sm text-red-600"
                    >
                      {{ validationErrors.payment_period_start }}
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Период оплаты (конец)
                    </label>
                    <input
                      v-model="form.payment_period_end"
                      type="date"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Email для документооборота
                    </label>
                    <input
                      v-model="form.mail_for_document_flow"
                      type="email"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Контакт для документооборота
                    </label>
                    <input
                      v-model="form.contact_for_document_flow"
                      type="text"
                      class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Скидка
                    </label>
                    <div class="relative flex items-center">
                      <input
                        v-model="form.discount"
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        placeholder="10"
                        class="w-full pr-8 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        :class="{ 'border-red-500': discountError }"
                        @input="validateDiscount"
                        @blur="validateDiscount"
                      />
                      <span
                        class="absolute right-3 text-gray-400 select-none pointer-events-none"
                      >
                        %
                      </span>
                    </div>
                    <div v-if="discountError" class="text-xs text-red-600 mt-1">
                      {{ discountError }}
                    </div>
                  </div>
                </div>

                <div class="md:col-span-2">
                  <label class="block text-sm font-medium text-gray-700 mb-1">
                    Комментарий
                  </label>
                  <textarea
                    v-model="form.comment"
                    rows="3"
                    placeholder="Введите комментарий к договору..."
                    class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  ></textarea>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div class="flex items-center">
                    <input
                      v-model="form.is_prepayment"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label class="ml-2 block text-sm text-gray-900">
                      Предоплата
                    </label>
                  </div>

                  <div class="flex items-center">
                    <input
                      v-model="form.active"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label class="ml-2 block text-sm text-gray-900">
                      Активен
                    </label>
                  </div>

                  <div class="flex items-center">
                    <input
                      v-model="form.is_send_email"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label class="ml-2 block text-sm text-gray-900">
                      Отправлять email
                    </label>
                  </div>

                  <div class="flex items-center">
                    <input
                      v-model="form.is_nds"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label class="ml-2 block text-sm text-gray-900">
                      НДС
                    </label>
                  </div>

                  <div class="flex items-center">
                    <input
                      v-model="form.is_tariff_by_limit"
                      type="checkbox"
                      class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label class="ml-2 block text-sm text-gray-900">
                      Тариф по лимиту
                    </label>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            @click="submitForm"
            :disabled="submitting || contractLoading || !isFormValid"
            class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div
              v-if="submitting"
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
            ></div>
            <div
              v-else-if="contractLoading"
              class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"
            ></div>
            {{ contractLoading ? 'Загрузка...' : mode === 'create' ? 'Создать' : 'Сохранить' }}
          </button>
          <button
            @click="$emit('close')"
            class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Отмена
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { ref, watch, computed } from 'vue'
  import { useToast } from 'primevue/usetoast'
  import {
    contractApi,
    counterpartyApi,
    paymentAccountApi
  } from '../services/api'
  import { CURRENCIES, ORGANIZATION_TYPES } from '../constants'

  const props = defineProps({
    show: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'create',
      validator: value => ['create', 'edit'].includes(value)
    },
    contractData: {
      type: Object,
      default: () => ({})
    }
  })

  const emit = defineEmits(['close', 'saved'])

  const toast = useToast()

  const form = ref({
    number_of_contract: '',
    date_of_contract: '',
    is_prepayment: false,
    deadline_after_invoice: 0,
    forecast_payment_after_invoice: 0,
    legal_name: '',
    active: true,
    counterparty_id: '',
    currency: '',
    is_send_email: false,
    is_nds: true,
    consulting_hourly_rate: 0,
    mail_for_document_flow: '',
    contact_for_document_flow: '',
    payment_period_start: '',
    payment_period_end: '',
    is_by_month: false,
    type_of_purchase: '',
    is_tariff_by_limit: false,
    type_organization: 'ООО',
    payment_account_id: '',
    payment_account_name: '',
    comment: '',
    discount: ''
  })

  const counterparties = ref([])
  const paymentAccounts = ref([])
  const submitting = ref(false)
  const counterpartiesLoading = ref(false)
  const paymentAccountsLoading = ref(false)
  const contractLoading = ref(false)
  const discountError = ref('')
  const validationErrors = ref({})

  const currencies = CURRENCIES
  const organizationTypes = ORGANIZATION_TYPES

  const isFormValid = computed(() => {
    return (
      form.value.number_of_contract?.trim() &&
      form.value.date_of_contract &&
      form.value.legal_name?.trim() &&
      form.value.counterparty_id &&
      form.value.payment_account_name?.trim() &&
      form.value.currency &&
      form.value.payment_period_start &&
      !discountError.value
    )
  })

  const validateRequiredFields = () => {
    validationErrors.value = {}
    let hasErrors = false

    if (
      !form.value.number_of_contract ||
      form.value.number_of_contract.trim() === ''
    ) {
      validationErrors.value.number_of_contract = 'Номер договора обязателен'
      hasErrors = true
    }

    if (!form.value.date_of_contract) {
      validationErrors.value.date_of_contract = 'Дата договора обязательна'
      hasErrors = true
    }

    if (!form.value.legal_name || form.value.legal_name.trim() === '') {
      validationErrors.value.legal_name = 'Юридическое название обязательно'
      hasErrors = true
    }

    if (!form.value.counterparty_id) {
      validationErrors.value.counterparty_id = 'Контрагент обязателен'
      hasErrors = true
    }

    if (
      !form.value.payment_account_name ||
      form.value.payment_account_name.trim() === ''
    ) {
      validationErrors.value.payment_account_name =
        'Платежный аккаунт обязателен'
      hasErrors = true
    }

    if (!form.value.currency) {
      validationErrors.value.currency = 'Валюта обязательна'
      hasErrors = true
    }

    if (!form.value.type_organization) {
      validationErrors.value.type_organization = 'Тип организации обязателен'
      hasErrors = true
    }

    if (!form.value.payment_period_start) {
      validationErrors.value.payment_period_start =
        'Период оплаты (начало) обязателен'
      hasErrors = true
    }

    return !hasErrors
  }

  const validateDiscount = () => {
    discountError.value = ''
    const val = form.value.discount

    if (val === '' || val === null || val === undefined) {
      return
    }

    const numVal = Number(val)
    if (isNaN(numVal) || numVal < 0 || numVal > 100) {
      discountError.value = 'Введите число от 0 до 100'
      return
    }
  }

  const updatePaymentAccountId = () => {
    if (form.value.payment_account_name) {
      const account = paymentAccounts.value.find(
        acc =>
          acc.name.toLowerCase() ===
          form.value.payment_account_name.toLowerCase()
      )
      if (account) {
        form.value.payment_account_id = account.id
      } else {
        form.value.payment_account_id = ''
      }
    } else {
      form.value.payment_account_id = ''
    }
  }

  const loadCounterparties = async () => {
    counterpartiesLoading.value = true
    try {
      const response = await counterpartyApi.getList({ is_deleted: 0 })
      if (response && Array.isArray(response)) {
        counterparties.value = response
      } else if (
        response &&
        response.counterparties &&
        Array.isArray(response.counterparties)
      ) {
        counterparties.value = response.counterparties
      } else if (response && response.data && Array.isArray(response.data)) {
        counterparties.value = response.data
      } else {
        counterparties.value = []
      }
    } catch (err) {
      counterparties.value = []
    } finally {
      counterpartiesLoading.value = false
    }
  }

  const loadPaymentAccounts = async () => {
    paymentAccountsLoading.value = true
    try {
      const response = await paymentAccountApi.getList({ limit: 1000 })
      if (response && Array.isArray(response)) {
        paymentAccounts.value = response
      } else if (
        response &&
        response.payment_accounts &&
        Array.isArray(response.payment_accounts)
      ) {
        paymentAccounts.value = response.payment_accounts
      } else if (response && response.data && Array.isArray(response.data)) {
        paymentAccounts.value = response.data
      } else {
        console.warn(
          'Unexpected payment accounts response structure:',
          response
        )
        paymentAccounts.value = []
      }
    } catch (err) {
      paymentAccounts.value = []
    } finally {
      paymentAccountsLoading.value = false
    }
  }

  const prepareFormData = data => {
    const prepared = { ...data }

    if (prepared.counterparty_id) {
      if (Array.isArray(prepared.counterparty_id)) {
        prepared.counterparty_id = prepared.counterparty_id[0] || null
      } else if (
        typeof prepared.counterparty_id === 'object' &&
        prepared.counterparty_id.id
      ) {
        prepared.counterparty_id = prepared.counterparty_id.id
      }
    } else {
      prepared.counterparty_id = null
    }

    if (prepared.payment_account_id) {
      if (Array.isArray(prepared.payment_account_id)) {
        prepared.payment_account_id = prepared.payment_account_id[0] || null
      } else if (
        typeof prepared.payment_account_id === 'object' &&
        prepared.payment_account_id.id
      ) {
        prepared.payment_account_id = prepared.payment_account_id.id
      }
    } else {
      prepared.payment_account_id = null
    }

    if (
      prepared.discount !== '' &&
      prepared.discount !== null &&
      prepared.discount !== undefined
    ) {
      prepared.discount = Number(prepared.discount)
    } else {
      prepared.discount = null
    }

    return prepared
  }

  const submitForm = async () => {
    const isFormValid = validateRequiredFields()
    validateDiscount()

    if (!isFormValid || discountError.value) {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: 'Пожалуйста, заполните все обязательные поля',
        life: 3000
      })
      return
    }

    submitting.value = true
    try {
      const formData = prepareFormData(form.value)

      if (props.mode === 'create') {
        await contractApi.create(formData)
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Договор создан',
          life: 3000
        })
      } else {
        await contractApi.update(props.contractData.id, formData)
        toast.add({
          severity: 'success',
          summary: 'Успешно',
          detail: 'Договор обновлен',
          life: 3000
        })
      }
      emit('close')
      emit('saved')
    } catch (err) {
      const errorData = err.response?.data
      let errorMessage = 'Произошла ошибка при сохранении'
      
      const details = errorData?.data?.details || errorData?.details
      
      if (details) {
        const firstField = Object.keys(details)[0]
        if (firstField) {
          const fieldError = details[firstField]
          if (Array.isArray(fieldError) && fieldError.length > 0) {
            errorMessage = `${firstField}: ${fieldError[0]}`
          } else if (typeof fieldError === 'string') {
            errorMessage = `${firstField}: ${fieldError}`
          }
        }
      } else if (errorData?.data?.message) {
        errorMessage = errorData.data.message
      } else if (errorData?.message) {
        errorMessage = errorData.message
      } else if (err.message) {
        errorMessage = err.message
      }
      
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: errorMessage,
        life: 5000
      })
    } finally {
      submitting.value = false
    }
  }

  const resetForm = () => {
    form.value = {
      number_of_contract: '',
      date_of_contract: '',
      is_prepayment: false,
      deadline_after_invoice: 0,
      forecast_payment_after_invoice: 0,
      legal_name: '',
      active: true,
      counterparty_id: '',
      currency: '',
      is_send_email: false,
      is_nds: true,
      consulting_hourly_rate: 0,
      mail_for_document_flow: '',
      contact_for_document_flow: '',
      payment_period_start: '',
      payment_period_end: '',
      is_by_month: false,
      type_of_purchase: '',
      is_tariff_by_limit: false,
      type_organization: 'ООО',
      payment_account_id: '',
      payment_account_name: '',
      comment: '',
      discount: ''
    }
    validationErrors.value = {}
    discountError.value = ''
  }

  watch(
    () => form.value.number_of_contract,
    () => {
      if (validationErrors.value.number_of_contract) {
        delete validationErrors.value.number_of_contract
      }
    }
  )

  watch(
    () => form.value.date_of_contract,
    () => {
      if (validationErrors.value.date_of_contract) {
        delete validationErrors.value.date_of_contract
      }
    }
  )

  watch(
    () => form.value.legal_name,
    () => {
      if (validationErrors.value.legal_name) {
        delete validationErrors.value.legal_name
      }
    }
  )

  watch(
    () => form.value.counterparty_id,
    () => {
      if (validationErrors.value.counterparty_id) {
        delete validationErrors.value.counterparty_id
      }
    }
  )

  watch(
    () => form.value.payment_account_name,
    () => {
      if (validationErrors.value.payment_account_name) {
        delete validationErrors.value.payment_account_name
      }
    }
  )

  watch(
    () => form.value.currency,
    () => {
      if (validationErrors.value.currency) {
        delete validationErrors.value.currency
      }
    }
  )

  watch(
    () => form.value.payment_period_start,
    () => {
      if (validationErrors.value.payment_period_start) {
        delete validationErrors.value.payment_period_start
      }
    }
  )

  const fillFormFromContract = (contract) => {
    if (!contract) return
    const formData = {
      ...contract,
      counterparty_id:
        contract.counterparty_id?.id ||
        contract.counterparty_id ||
        '',
      payment_account_id:
        contract.payment_account_id?.id ||
        contract.payment_account_id ||
        ''
    }
    form.value = formData
    if (formData.payment_account_id) {
      const account = paymentAccounts.value.find(
        acc => acc.id === formData.payment_account_id
      )
      if (account) {
        form.value.payment_account_name = account.name
      }
    }
  }

  const loadContractById = async (id) => {
    contractLoading.value = true
    try {
      const contract = await contractApi.getById(id)
      fillFormFromContract(contract)
    } catch (err) {
      toast.add({
        severity: 'error',
        summary: 'Ошибка',
        detail: err.message || 'Не удалось загрузить договор',
        life: 3000
      })
    } finally {
      contractLoading.value = false
    }
  }

  watch(
    () => props.show,
    async newVal => {
      if (newVal) {
        loadCounterparties()
        await loadPaymentAccounts()
        if (props.mode === 'edit' && props.contractData?.id) {
          await loadContractById(props.contractData.id)
        } else if (props.mode === 'edit' && props.contractData && !props.contractData.id) {
          fillFormFromContract(props.contractData)
        } else {
          resetForm()
        }
      }
    }
  )
</script>
